/**
 * First-open seeding of a brand-new board: one default sheet, 2 columns ×
 * 3 rows (F-10, amended 2026-07-10: new boards start tiny and grow via the
 * F-13 add affordances), the leftmost of which is the locked "Original
 * Script" source column (F-28, 2026-07-15).
 *
 * This MUST run only on the server, inside Hocuspocus `onLoadDocument`
 * (single instance ⇒ doc loads are serialized), never on clients. Two clients
 * bootstrapping concurrently would each lazily create the same nested
 * sheet/colMeta/rowMeta entry Y.Maps; the entries themselves converge (same
 * deterministic ids/ords), but any edit made INTO the losing client's entry
 * before the merge arrives is silently discarded when Yjs resolves the
 * map-key conflict by deleting the losing subtree — the exact C2
 * lazily-created-subtree failure mode the flat cell keys exist to avoid
 * (see schema.ts `cellKey`). Server-side seeding removes the race entirely
 * and keeps read-only roles from ever emitting bootstrap writes.
 */
import { SHORT_COLUMN_NAME, SHORT_COLUMN_WIDTH, SHORT_ROWS_PER_SLOT, SHORT_SLOT_COLORS, SHORTS_PER_BOARD, } from "../shorts.js";
export const DEFAULT_SHEET_ID = "s-main";
export const DEFAULT_SHEET_NAME = "Sheet 1";
export const DEFAULT_COLS = 2;
export const DEFAULT_ROWS = 3;
/**
 * F-28: the auto-name of every new board's leftmost column — the canonical
 * translation source (F-70). Seeded LOCKED, which is what makes the name
 * canonical: the accessors refuse to rename, delete or reorder it, so
 * `sourceColumnId` always resolves and the F-70 toolbar always has a source.
 */
export const SOURCE_COLUMN_NAME = "Original Script";
/**
 * F-154: the columns a one-Short board has after the locked source — the
 * writer's table for a Short is the script, its English, and the visual and
 * sound direction, nothing else. A file imported at creation lands in these.
 */
export const SHORT_BOARD_COLUMN_NAMES = ["English", "Visual and Sound Direction"];
/** Spreadsheet-style column label: 0 → "A", 25 → "Z", 26 → "AA", … */
export function columnLabel(index) {
    let n = index;
    let label = "";
    do {
        label = String.fromCharCode(65 + (n % 26)) + label;
        n = Math.floor(n / 26) - 1;
    } while (n >= 0);
    return label;
}
/** Deterministic seeded column id: "c000", "c001", … */
export function seededColumnId(index) {
    return `c${String(index).padStart(3, "0")}`;
}
/** Deterministic seeded row id: "r0000", "r0001", … */
export function seededRowId(index) {
    return `r${String(index).padStart(4, "0")}`;
}
/**
 * Seed the default sheet if the board has none. One transaction (one sync
 * unit). Returns true when seeding happened.
 */
export function seedDefaultSheet(board, origin, options = {}) {
    if (board.listSheets().length > 0)
        return false;
    const shortSlots = options.shortSlots ?? SHORTS_PER_BOARD;
    if (options.kind === "shorts" && shortSlots > 1) {
        seedShortsWeekSheet(board, shortSlots, origin);
        return true;
    }
    const isShort = options.kind === "shorts";
    const rows = isShort ? SHORT_ROWS_PER_SLOT : DEFAULT_ROWS;
    const cols = isShort ? 1 + SHORT_BOARD_COLUMN_NAMES.length : DEFAULT_COLS;
    board.transact(() => {
        board.createSheet(DEFAULT_SHEET_ID, DEFAULT_SHEET_NAME, origin);
        for (let c = 0; c < cols; c++) {
            // F-28: the FIRST column is the locked "Original Script" source column;
            // the rest keep the default positional label (a Short's are named,
            // F-154) and stay fully editable.
            const isSource = c === 0;
            board.addColumn(DEFAULT_SHEET_ID, seededColumnId(c), {
                index: c,
                name: isSource ? SOURCE_COLUMN_NAME : isShort ? (SHORT_BOARD_COLUMN_NAMES[c - 1] ?? "") : columnLabel(c),
                ...(isSource ? { locked: true } : {}),
                origin,
            });
        }
        for (let r = 0; r < rows; r++) {
            board.addRow(DEFAULT_SHEET_ID, seededRowId(r), { index: r, origin });
        }
    }, origin);
    return true;
}
/** Deterministic id of the seeded `Short` column — the one after the source. */
export const SHORT_COLUMN_ID = seededColumnId(1);
/**
 * F-140: the sheet a week of Shorts starts with — the locked "Original
 * Script" column, then `Short`, and `slots × SHORT_ROWS_PER_SLOT` rows whose
 * `Short` cell already says which slot the line belongs to, filled in that
 * slot's colour. The contiguous runs that makes are the board's shorts; a
 * writer types into the rows of a run and never has to label a line. Since
 * F-143 only a five-short week made before it (and never opened) is seeded
 * this way — a new Shorts board holds one Short and needs no labels.
 *
 * Same server-only, single-transaction contract as `seedDefaultSheet` (which
 * is the only caller). The slot value is written with `setCellValue`, not the
 * human variant: nothing about a seed is a person's edit, and there is no
 * translation status to clear on a cell that did not exist a moment ago.
 */
export function seedShortsWeekSheet(board, slots, origin) {
    board.transact(() => {
        board.createSheet(DEFAULT_SHEET_ID, DEFAULT_SHEET_NAME, origin);
        board.addColumn(DEFAULT_SHEET_ID, seededColumnId(0), {
            index: 0,
            name: SOURCE_COLUMN_NAME,
            locked: true,
            origin,
        });
        board.addColumn(DEFAULT_SHEET_ID, SHORT_COLUMN_ID, {
            index: 1,
            name: SHORT_COLUMN_NAME,
            width: SHORT_COLUMN_WIDTH,
            origin,
        });
        let r = 0;
        for (let slot = 1; slot <= slots; slot++) {
            const color = SHORT_SLOT_COLORS[(slot - 1) % SHORT_SLOT_COLORS.length];
            for (let i = 0; i < SHORT_ROWS_PER_SLOT; i++, r++) {
                const rowId = seededRowId(r);
                board.addRow(DEFAULT_SHEET_ID, rowId, { index: r, origin });
                board.setCellValue(DEFAULT_SHEET_ID, rowId, SHORT_COLUMN_ID, String(slot), origin);
                board.setCellBg(DEFAULT_SHEET_ID, rowId, SHORT_COLUMN_ID, color, origin);
            }
        }
    }, origin);
}
//# sourceMappingURL=seed.js.map